import { useEffect, useMemo, useRef, useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'

import { useIntersection } from 'react-use'

import NavTextNodes from '@data/i18n/nav.json'

import { getMagContent } from '@lib/prismic/fetch/documents'
import { formatDateString } from '@lib/utils/dates'

type Props = {
	initialPosts: any[]
	topicID: string
	totalPages: number
}

function MagazineArticleList({ initialPosts, topicID, totalPages }: Props) {
	// Track lazy-loaded posts
	const [posts, setPosts] = useState(initialPosts)
	useEffect(() => {
		setPosts(initialPosts)
	}, [initialPosts])

	const [currPage, setCurrPage] = useState(1)
	const lastPostID: string = useMemo(() => posts?.[posts.length - 1]?.id, [posts])

	// IO Logic
	const [isLoadingNewPosts, setIsLoadingNewPosts] = useState(false)
	const intersectionRef = useRef<HTMLDivElement>(null)
	const { isIntersecting } = useIntersection(intersectionRef, {}) || {}
	useEffect(() => {
		if (intersectionRef?.current && isIntersecting && !isLoadingNewPosts && currPage < totalPages) {
			;(async () => {
				setIsLoadingNewPosts(true)

				const { results: nextPosts } =
					(await getMagContent({
						topicID,
						after: lastPostID
					})) || {}
				setPosts([...posts, ...(Array.isArray(nextPosts) ? nextPosts : [])])

				setCurrPage(currPage + 1)

				setIsLoadingNewPosts(false)
			})()
		}
	}, [currPage, totalPages, isIntersecting, posts, lastPostID, topicID, isLoadingNewPosts])

	return (
		<>
			{Array.isArray(posts) ? (
				<section className="section pt-8">
					<div className="container">
						{/* Show all posts in order */}
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mx-8">
							{posts.map((post: any, i) => {
								const { id, uid, data } = post || {}

								return uid ? (
									<div className={`${i === 0 ? 'md:col-span-2 lg:col-span-3' : ''}`} key={id}>
										<Link legacyBehavior href={`${NavTextNodes.theMagUrl}/article/${uid}`}>
											<a className={`p-8 block group ${i === 0 ? 'md:flex' : ''}`}>
												{/* Image */}
												<div
													className={`bg-gray-100 h-[180px] md:h-[380px] relative ${
														i === 0 ? 'md:w-1/2' : 'mb-8'
													}`}
												>
													{data.hero_image.thumb.url ? (
														// Image
														<Image
															src={data.hero_image.thumb.url}
															layout="fill"
															objectFit="cover"
															alt={data.hero_image.alt || data.title?.[0]?.text || 'Magazine article image'}
														/>
													) : (
														// No image
														<div className="h-full flex items-center justify-center">
															<i className="fak text-gray-300 fa-birchbox text-6xl" />
														</div>
													)}
												</div>

												<div
													className={`flex-1 ${
														i === 0
															? 'md:flex md:flex-col md:items-start md:justify-center md:p-12'
															: ''
													}`}
												>
													{/* Metadata */}
													<p className="mb-2 font-medium text-sm text-gray-500">
														{data?.topics?.[0]?.topic?.data?.title?.[0]?.text && (
															<>
																<span className="">{data.topics[0].topic.data.title[0].text}</span>{' '}
																&middot;{' '}
															</>
														)}
														<time>{formatDateString(data.date)}</time>
													</p>

													{/* Title */}
													<h3 className="title text-3xl mb-4">{data.title[0].text}</h3>

													{/* Description */}
													{post?.data?.description?.[0]?.text && (
														<p className="max-md:line-clamp-3">{data.description[0].text}</p>
													)}

													{/* Read more - A11Y: thicker underline provides non-color visual cue for link identification per WCAG 1.4.1 */}
													<span className="button text-bbx-spark group-hover:text-bbx-spark p-0 border-0 mt-6">
														<span className="underline decoration-2 underline-offset-2">Read more</span> <i className="fa-regular fa-right-long text-sm ml-1" />
													</span>
												</div>
											</a>
										</Link>
									</div>
								) : null
							})}
						</div>
					</div>
				</section>
			) : null}

			{/* Load more posts */}
			<p className="text-center py-6" aria-hidden="true" ref={intersectionRef}>
				{currPage < totalPages ? 'Load more' : 'All posts loaded'}
			</p>
		</>
	)
}

export default MagazineArticleList
